
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import ReserveButton from './ReserveButton';
import NavbarLogo from './navbar/NavbarLogo';
import DesktopNavigation from './navbar/DesktopNavigation';
import MobileMenu from './navbar/MobileMenu';
import SearchOverlay from './navbar/SearchOverlay';
import { searchData } from './navbar/searchData';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{name: string, path: string, type?: string}>>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const navbarRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [menuHeight, setMenuHeight] = useState('100vh');

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Calculate available height for mobile menu
  useEffect(() => {
    if (navbarRef.current) {
      const navbarHeight = navbarRef.current.offsetHeight;
      setMenuHeight(`calc(100vh - ${navbarHeight}px)`);
    }
  }, [isScrolled]);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setSearchQuery('');
  }, [location.pathname]);
  
  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Focus search input when search is opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isSearchOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isSearchOpen) setIsSearchOpen(false);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isMenuOpen) setIsMenuOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.length > 1) {
      // Search tours
      const matchingTours = searchData.tours
        .filter(tour => tour.name.toLowerCase().includes(query.toLowerCase()))
        .map(tour => ({...tour, type: 'tour'}));
      
      // Search pages
      const matchingPages = searchData.pages
        .filter(page => page.name.toLowerCase().includes(query.toLowerCase()))
        .map(page => ({...page, type: 'page'}));
      
      setSearchResults([...matchingTours, ...matchingPages]);
    } else {
      setSearchResults([]);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      navigate(searchResults[0].path);
      toggleSearch();
    }
  };

  return (
    <header 
      ref={navbarRef}
      className={cn(
        'sticky w-full top-0 z-50 transition-all duration-500',
        isScrolled 
          ? 'bg-[#F5F5DC]/95 shadow-elegant supports-backdrop-blur:backdrop-blur-sm h-16' 
          : 'bg-transparent h-20'
      )}
      role="banner"
    >
      <div className="container mx-auto px-4 h-full">
        <nav className="flex items-center justify-between h-full" aria-label="Main navigation">
          <NavbarLogo isScrolled={isScrolled} />

          <DesktopNavigation 
            toggleSearch={toggleSearch}
            isSearchOpen={isSearchOpen}
          />

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={toggleSearch}
              className="p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#657C56] text-[#3E2723] hover:text-[#657C56]"
              aria-expanded={isSearchOpen}
              aria-label="Search website"
            >
              <Search size={20} aria-hidden="true" />
            </button>
            <ReserveButton 
              className="text-sm py-2 px-3"
              showCalendarIcon={false}
            />
            <button 
              onClick={toggleMenu}
              className="p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#657C56] text-[#3E2723] hover:text-[#657C56] ml-1"
              aria-expanded={isMenuOpen}
              aria-label="Toggle menu"
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          </div>
        </nav>
      </div>

      <SearchOverlay 
        isSearchOpen={isSearchOpen}
        searchQuery={searchQuery}
        searchResults={searchResults}
        searchInputRef={searchInputRef}
        toggleSearch={toggleSearch}
        handleSearchChange={handleSearchChange}
        handleSearchSubmit={handleSearchSubmit}
      />

      <MobileMenu 
        isMenuOpen={isMenuOpen}
        menuHeight={menuHeight}
        navbarRef={navbarRef}
        closeMenu={closeMenu}
      />
    </header>
  );
};

export default Navbar;
